import React, { useState, useEffect, useCallback } from "react";
import { Typography, Box } from '@mui/material';
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import SearchCriteria from "../../Utilities/SearchCriteria";
import PrimaryContent from '../../Utilities/PrimaryContent';
import NCRTable from "../../Utilities/NCRTable";
import CircularLoader from "../../Common/CircularLoader";
import { validateAndRetrieveNCROtherFields } from "../../../Helpers/NCRHelpers";

export default function NCProTableComponents(data) {
  const location = useLocation();
  const { targetData } = useSelector((state) => state.target || { targetData: {} });

  const [formData, setFormData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dateRangeFilter, setDateRangeFilter] = useState('');
  const [additionalFilters, setAdditionalFilters] = useState({});

  const getFromData = useCallback(async () => {
    try {
      setIsLoading(true);
      const accessToken = localStorage.getItem('NCProAccessToken');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/ncr/stages/${data?.label}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const processedData = validateAndRetrieveNCROtherFields(response.data.ncrForm || []);
      setFormData(processedData);
    } catch (error) {
      console.error('Error fetching ncr:', error);
    } finally {
      setIsLoading(false);
    }
  }, [data?.label]);

  const filterDataByAging = (data, status) => {
    if (!data || data.length === 0) return [];

    const target = status === "In Action" ? targetData.aging_target_action : targetData.aging_target_review;

    if (!target || target <= 0) return data;

    return data.filter(item => {
      if (item.status !== status) return false;

      const currentDate = new Date(new Date().toISOString());
      const ncrDate = new Date(item.date_of_issue);
      currentDate.setHours(0, 0, 0, 0);
      ncrDate.setHours(0, 0, 0, 0);

      const diffTime = Math.abs(currentDate - ncrDate);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      return diffDays > target;
    });
  };

  const getFilteredData = async (filterValue) => {
    try {
      setIsLoading(true);
      const accessToken = localStorage.getItem("NCProAccessToken");
      const filterValueToSend = { ...filterValue };
      const hasAgingFilter = filterValueToSend.aging === true;
      const agingStatus = filterValueToSend.status;

      // Remove aging flag before sending to backend (it's a client-side filter)
      delete filterValueToSend.aging;

      const queryParams = new URLSearchParams(filterValueToSend).toString();

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/ncr/stages/${data?.label}/?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      let processedData = validateAndRetrieveNCROtherFields(response.data.ncrForm || []);

      if (hasAgingFilter && agingStatus) {
        processedData = filterDataByAging(processedData, agingStatus);
      }
      setFormData(processedData);
    } catch (error) {
      console.error("Error fetching filtered NCR data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchCriteriaChange = async (filterValue) => await getFilteredData(filterValue);

  useEffect(() => {
    // Check if coming from dashboard with date range filter
    if (location.state?.fromDashboard && location.state?.dateRange) {
      setDateRangeFilter(location.state.dateRange);

      // Set additional filters if provided
      if (location.state?.additionalFilters) {
        setAdditionalFilters(location.state.additionalFilters);
      }

      // Build filter object with date range and additional filters
      const filterObj = { date_range: location.state.dateRange };
      if (location.state?.additionalFilters) {
        Object.assign(filterObj, location.state.additionalFilters);
      }

      getFilteredData(filterObj);
    } else {
      getFromData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, getFromData]);

  return (
    <Box sx={{ pt: 10 }}>
      <Box>
        <Typography sx={{
          fontFamily: 'Lato',
          fontSize: '18px',
          fontWeight: 700,
          my: 2,
          px: 0.5
        }}>
          {data?.label === "view" ? "View" : (data?.label === "action" && data?.newPage) ? "RC Analysis" : data?.label === "action" ? "Action" : data?.label === "edit" ? "Edit" : data?.label === "review" ? "Review" : data?.label === "closure" ? "Closure" : "Data table"}
        </Typography>
      </Box>
      <SearchCriteria onChange={handleSearchCriteriaChange} initialDateRange={dateRangeFilter} initialFilters={additionalFilters} />
      {isLoading ? (
        <CircularLoader />
      ) : formData.length === 0 ? (
        <PrimaryContent
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "350px",
            textAlign: "center",
          }}
        >
          No records found
        </PrimaryContent>
      ) : (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <NCRTable data={{ formData, page: data?.label, newPage: data?.newPage }} />
        </Box>
      )}
    </Box >
  );
};